import React from 'react';
import debounce from 'lodash.debounce';
import { useDispatch } from 'react-redux';

import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import { evFacetClick, evFacetSearch } from 'events/search';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import useMartyContext from 'hooks/useMartyContext';
import type { AutoCompleteDataType, FacetDataType, FacetsDataType } from 'types/search';
import { toggleSelectedFacetGroup } from 'actions/facets';

interface FacetSelectOptions {
  facetGroup: string;
  facetName: string;
  selectedFacetGroupIndex: number;
  selectedFacetIndex: number;
  isSelected: boolean;
  facetZsoUrl?: string;
}

interface FacetsV2Props {
  facets: FacetsDataType;
  autoComplete: AutoCompleteDataType;
  hasAutoComplete?: boolean;
  onFacetSelect: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
  getFacetUrlPath: (url?: string) => void;
  getFacetUrlParams: (url?: string) => void;
}

export const FacetsV2 = ({ facets: facetData, autoComplete, hasAutoComplete, onFacetSelect, getFacetUrlPath, getFacetUrlParams }: FacetsV2Props) => {
  const { testId } = useMartyContext();
  const dispatch = useDispatch();

  const facets = facetData.navigationV2 ?? [];

  const handleFacetSelect = (options: FacetSelectOptions): boolean => {
    const { facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isSelected, facetZsoUrl } = options;

    getFacetUrlParams(facetZsoUrl);
    getFacetUrlPath(facetZsoUrl);

    onFacetSelect(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex);

    track(() => [
      evFacetClick,
      {
        facetGroup,
        facetName,
        facetClickSource: 'LEFT_HAND_FACET_NAV',
        selected: !isSelected,
        deselected: !!isSelected
      }
    ]);

    // track if autocomplete used for this group
    if (hasAutoComplete && autoComplete[facetGroup]?.text) {
      trackEvent('TE_SEARCH_AUTOCOMPLETE', `${facetGroup}:${autoComplete[facetGroup]?.text || ''}`);
    }

    window.scrollTo(0, 0);
    return false;
  };

  const debouncedSearchEvent = debounce((facetGroup: FacetDataType, searchText: string): void => {
    track(() => [
      evFacetSearch,
      {
        autoComplete,
        facetGroup,
        term: searchText
      }
    ]);
  }, 1000);

  const handleAccordionChange = (expandedValues: string[]): void => {
    // Find which facet group changed by comparing current vs new expanded values
    const currentExpanded = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);

    // Find the difference to determine which facet was toggled
    const added = expandedValues.find(value => !currentExpanded.includes(value));
    const removed = currentExpanded.find(value => !expandedValues.includes(value));

    const toggledFacet = added || removed;

    if (toggledFacet) {
      dispatch(toggleSelectedFacetGroup(toggledFacet));
    }
  };

  const expandedValues = facets.filter(facet => facet.isExpanded).map(facet => facet.facetField);

  return (
    <FacetAccordion.Root type="multiple" value={expandedValues} onValueChange={handleAccordionChange} className="max-md:px-5">
      {facets.map((facet, facetIndex) => {
        const { facetField, facetFieldDisplayName, values } = facet;

        return (
          <FacetAccordion.Item key={facetField} value={facetField} data-test-id={testId('facetGroup')} facet={facet}>
            <FacetAccordion.Trigger id={facetField} data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />

              <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
            </FacetAccordion.Trigger>

            <FacetAccordion.Content>
              {hasAutoComplete && (
                <FacetAccordion.AutoComplete onChange={(e: React.ChangeEvent<HTMLInputElement>) => debouncedSearchEvent(facet, e.target.value)} />
              )}

              <FacetAccordion.ValueList data-test-id={testId(`${facetFieldDisplayName}-values`)} autoComplete={autoComplete}>
                {({ facetValue }) => (
                  <FacetAccordion.Value
                    data-test-id={testId(`facet-${facetValue.name}`)}
                    facetValue={facetValue}
                    onFacetSelect={() => {
                      const valueIndex = values.findIndex(value => value.name === facetValue.name);
                      const facetUrl = facetValue?.facetUrl || facetValue?.facetZsoUrl;

                      handleFacetSelect({
                        facetGroup: facetField,
                        facetName: facetValue.name,
                        selectedFacetGroupIndex: facetIndex,
                        selectedFacetIndex: valueIndex,
                        isSelected: facetValue?.selected || false,
                        facetZsoUrl: facetUrl
                      });
                    }}
                  />
                )}
              </FacetAccordion.ValueList>
            </FacetAccordion.Content>
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};

FacetsV2.displayName = 'FacetsV2';

const FacetsWithErrorBoundary = withErrorBoundary('FacetsV2', FacetsV2);
export default FacetsWithErrorBoundary;
